import { useEffect, useRef } from 'react';
import application from 'application';
import { startupLazyRouteImporters } from './startup-lazy-routes';

function StartupRouteWarmup(props) {
  const {
    startup,
    authenticated,
    pathname,
    pathPrefix
  } = props;

  const warmedStartupIdReference = useRef(null);

  useEffect(function warmRoutesAfterInitialLoad() {

    if (startup === null) {
      return;
    }

    if (warmedStartupIdReference.current === startup._id) {
      return;
    }

    let cancelled = false;
    let idleCallbackId = null;
    let timeoutId = null;

    function normalizeRelativePathname() {
      let relativePathname = pathname || '';

      if (pathPrefix && relativePathname.indexOf(pathPrefix) === 0) {
        relativePathname = relativePathname.slice(pathPrefix.length);
      }

      if (relativePathname === '') {
        return '/';
      }

      if (relativePathname[0] !== '/') {
        return `/${relativePathname}`;
      }

      return relativePathname;
    }

    function matchesRoute(relativePathname, routePrefix) {
      return (
        relativePathname === routePrefix ||
        relativePathname.indexOf(`${routePrefix}/`) === 0
      );
    }

    function addIfNeeded(queue, relativePathname, routePrefix, importer) {
      if (matchesRoute(relativePathname, routePrefix) === true) {
        return;
      }

      queue.push(importer);
    }

    function buildQueue() {
      const queue = [];
      const relativePathname = normalizeRelativePathname();
      const canViewCustomersSection = application.accessController.isAllowedToViewCustomersSection();
      const isMemberOrAdministrator = application.accessController.isMemberOrAdministrator();

      addIfNeeded(queue, relativePathname, '/community', startupLazyRouteImporters.community);

      if (authenticated === true) {
        addIfNeeded(queue, relativePathname, '/pages', startupLazyRouteImporters.pages);
      }

      if (authenticated === true && isMemberOrAdministrator === true) {
        addIfNeeded(queue, relativePathname, '/welcome', startupLazyRouteImporters.welcome);
      }

      if (canViewCustomersSection === true) {
        addIfNeeded(queue, relativePathname, '/analytics', startupLazyRouteImporters.dashboard);
        addIfNeeded(queue, relativePathname, '/contacts', startupLazyRouteImporters.customers);
        addIfNeeded(queue, relativePathname, '/campaigns', startupLazyRouteImporters.campaigns);
        addIfNeeded(queue, relativePathname, '/inbox', startupLazyRouteImporters.inbox);
        addIfNeeded(queue, relativePathname, '/databases', startupLazyRouteImporters.dataCollections);
        addIfNeeded(queue, relativePathname, '/products', startupLazyRouteImporters.products);
        addIfNeeded(queue, relativePathname, '/skills', startupLazyRouteImporters.skills);

        if (application.isPaymentsModuleEnabled() === true) {
          addIfNeeded(queue, relativePathname, '/finance', startupLazyRouteImporters.payments);
        }

        if (application.isCalendarModuleEnabled() === true) {
          addIfNeeded(queue, relativePathname, '/tasks', startupLazyRouteImporters.calendar);
        }
      }

      return queue;
    }

    function warmSequentially(queue) {
      let index = 0;

      function loadNext() {
        if (cancelled === true) {
          return;
        }

        if (index >= queue.length) {
          return;
        }

        const importer = queue[index];
        index += 1;

        Promise.resolve(importer())
          .catch(function ignoreWarmupError() {})
          .finally(function scheduleNext() {
            if (cancelled === true) {
              return;
            }

            timeoutId = window.setTimeout(loadNext, 150);
          });
      }

      loadNext();
    }

    function startWarmup() {
      if (cancelled === true) {
        return;
      }

      if (warmedStartupIdReference.current === startup._id) {
        return;
      }

      warmedStartupIdReference.current = startup._id;
      warmSequentially(buildQueue());
    }

    if (typeof window.requestIdleCallback === 'function') {
      idleCallbackId = window.requestIdleCallback(startWarmup, { timeout: 2000 });
    } else {
      timeoutId = window.setTimeout(startWarmup, 300);
    }

    return function cleanup() {
      cancelled = true;

      if (idleCallbackId !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleCallbackId);
      }

      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
    };

  }, [startup, authenticated, pathname, pathPrefix]);

  return null;
}

export default StartupRouteWarmup;